/** @jsxImportSource @emotion/react */
import { useDraggable } from '@dnd-kit/core';
import { usePhotoUriForLayoutSize } from '@kaiizen/kai-pro-api-react-state';

import { CameraIcon, UploadIcon } from '@kaiizen/kai-shiki/react';

import type { GalleryPhotoOccurrence } from '../../FocusedPhotoGallery/galleryPhotoOperations';
import { getPhotoTileLabel, getTruncatedChipLabel } from './focusedStripPhotoTileLib';
import { FOCUSED_STRIP_PHOTO_TILE_WIDTH_PX, type FocusedStripStyles } from './focusedStripStyles';
import {
  type FocusedPhotoStripEntry,
  GROUP_PHOTO_CHIP_LABEL,
  TASK_PHOTO_CHIP_LABEL_FALLBACK,
} from './focusedStripTypes';

export const FocusedStripPhotoTile = ({
  photo,
  authorName,
  showTaskChipLabels,
  selected,
  dragSources,
  styles,
  onClick,
}: {
  photo: FocusedPhotoStripEntry;
  authorName: string | undefined;
  showTaskChipLabels: boolean;
  selected: boolean;
  dragSources: readonly GalleryPhotoOccurrence[];
  styles: FocusedStripStyles;
  onClick: (event: React.MouseEvent) => void;
}) => {
  const uri = usePhotoUriForLayoutSize(photo.photoId, FOCUSED_STRIP_PHOTO_TILE_WIDTH_PX);
  const isUploaded = photo.photoSource === 'upload';
  const showMetaOverlay = !!(photo.walkLabel || authorName || photo.photoSource);
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `focused-photo-strip-tile-${photo.photoId}-${photo.groupId ?? ''}-${photo.taskId ?? ''}`,
    data: {
      type: 'photo',
      uri,
      sources: dragSources,
    },
  });

  return (
    <figure css={styles.figure}>
      <button
        ref={setNodeRef}
        {...attributes}
        {...listeners}
        type={'button'}
        css={[styles.photoTile.tile, selected && styles.photoTile.selected]}
        data-testid={'focused-photo-tile'}
        data-photo-id={photo.photoId}
        data-selected={selected ? 'true' : 'false'}
        aria-label={getPhotoTileLabel(photo, authorName)}
        aria-pressed={selected}
        style={{
          ...(uri ? { backgroundImage: `url(${uri})` } : undefined),
          opacity: isDragging ? 0.5 : 1,
        }}
        onClick={onClick}
      >
        {photo.scope === 'group'
          ? (
            <span css={styles.photoTile.chip}>
              {getTruncatedChipLabel(photo.groupLabel ?? GROUP_PHOTO_CHIP_LABEL)}
            </span>
          )
          : showTaskChipLabels
          ? (
            <span css={styles.photoTile.chip}>
              {photo.taskLabel
                ? getTruncatedChipLabel(photo.taskLabel)
                : TASK_PHOTO_CHIP_LABEL_FALLBACK}
            </span>
          )
          : null}
        {showMetaOverlay
          ? (
            <span css={styles.photoTile.metaOverlay} aria-hidden={'true'}>
              {photo.walkLabel ? <span css={styles.photoTile.metaWalk}>{photo.walkLabel}</span> : null}
              <span css={styles.photoTile.metaRow}>
                <span css={styles.photoTile.metaBy}>
                  {authorName ? `${isUploaded ? 'Uploaded' : 'Captured'} · ${authorName}` : ''}
                </span>
                {photo.photoSource
                  ? (
                    isUploaded
                      ? <UploadIcon size={14} aria-hidden={'true'} />
                      : <CameraIcon size={14} aria-hidden={'true'} />
                  )
                  : null}
              </span>
            </span>
          )
          : null}
      </button>
      {photo.dateLabel ? <span css={styles.caption}>{photo.dateLabel}</span> : null}
    </figure>
  );
};
